import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ArrowIcon } from '@/shared/icons/arrow-down.svg';

interface PaginationProps {
  totalItems: number;
  itemsPerPage: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  totalItems,
  itemsPerPage,
  currentPage: propCurrentPage,
  onPageChange
}) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = propCurrentPage || Number(searchParams.get('page')) || 1;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePageChange = (page: number) => {
    if (onPageChange) {
      onPageChange(page);
    } else {
      setSearchParams({ page: page.toString() });
    }
  };

  if (totalPages <= 1) return null;

  const renderPageNumbers = () => {
    const pages = [];
    const showEllipsis = totalPages > 7;

    if (showEllipsis) {
      // Всегда показываем первую страницу
      pages.push(1);

      // Показываем многоточие в начале если текущая страница далеко от начала
      if (currentPage > 4) {
        pages.push('...');
      }

      // Показываем страницы вокруг текущей
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }

      // Показываем многоточие в конце если текущая страница далеко от конца
      if (currentPage < totalPages - 3) {
        pages.push('...');
      }

      // Всегда показываем последнюю страницу
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    } else {
      // Если страниц мало, показываем все
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    }

    return pages.map((page, index) => {
      if (page === '...') {
        return (
          <span key={`ellipsis-${index}`} className="px-2 py-2 text-text-tertiary">
            {t('pagination.ellipsis')}
          </span>
        );
      }

      return (
        <button
          key={page}
          onClick={() => handlePageChange(page as number)}
          data-testid="pagination-page-button"
          className={`min-w-[40px] h-10 rounded-lg text-sm ${
            currentPage === page
              ? 'bg-brand-500 text-white'
              : 'pagination-button'
          }`}
        >
          {page}
        </button>
      );
    });
  };

  return (
    <div className="flex justify-center items-center gap-1 md:gap-2">
      <button
        data-testid="pagination-previous-button"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="pagination-button flex items-center justify-center rounded-lg text-sm disabled:opacity-50 w-10 h-10 md:w-auto md:h-auto md:px-4 md:py-2"
      >
        <ArrowIcon className="w-4 h-4 rotate-90 fill-current md:hidden" />
        <span className="hidden md:inline">{t('pagination.previous')}</span>
      </button>

      <div className="flex gap-1 md:gap-2">
        {renderPageNumbers()}
      </div>

      <button
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        data-testid="pagination-next-button"
        className="pagination-button flex items-center justify-center rounded-lg text-sm disabled:opacity-50 w-10 h-10 md:w-auto md:h-auto md:px-4 md:py-2"
      >
        <ArrowIcon className="w-4 h-4 -rotate-90 fill-current md:hidden" />
        <span className="hidden md:inline">{t('pagination.next')}</span>
      </button>
    </div>
  );
};

export default Pagination;
